// SPDX-License-Identifier: AGPL-3.0-or-later

import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import * as TemplateCommands from '@app/features/guild/commands/TemplateCommands';
import {
	EmbedCard,
	EmbedSkeletonButton,
	EmbedSkeletonCircle,
	EmbedSkeletonSubtitle,
	EmbedSkeletonTitle,
} from '@app/features/messaging/components/embeds/embed_card/EmbedCard';
import cardStyles from '@app/features/messaging/components/embeds/embed_card/EmbedCard.module.css';
import {Button} from '@app/features/ui/button/Button';
import type {GuildTemplateResponse} from '@fluxer/schema/src/domains/guild/GuildTemplateSchemas';
import {msg} from '@lingui/core/macro';
import {Plural, useLingui} from '@lingui/react/macro';
import {CopyIcon, WarningCircleIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';
import styles from '@app/features/channel/components/TemplateEmbed.module.css';

const SERVER_TEMPLATE_DESCRIPTOR = msg({
	message: 'Server Template',
	comment: 'Subtitle on the chat embed card for server templates.',
});

const USE_TEMPLATE_DESCRIPTOR = msg({
	message: 'Use Template',
	comment: 'Button label on the chat embed card to preview and create a server from this template.',
});

const TEMPLATE_UNAVAILABLE_TITLE_DESCRIPTOR = msg({
	message: 'Unknown Template',
	comment: 'Title on the chat embed card when the template does not exist or has expired.',
});

const TEMPLATE_UNAVAILABLE_SUBTITLE_DESCRIPTOR = msg({
	message: 'This template is no longer available.',
	comment: 'Subtitle on the chat embed card when the template does not exist or has expired.',
});

interface TemplateEmbedProps {
	code: string;
}

export const TemplateEmbed: React.FC<TemplateEmbedProps> = observer(({code}) => {
	const {i18n} = useLingui();
	const [template, setTemplate] = useState<GuildTemplateResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	useEffect(() => {
		let active = true;
		setLoading(true);
		setError(false);

		GuildCommands.fetchTemplateByCode(code)
			.then((res) => {
				if (active) {
					setTemplate(res);
					setLoading(false);
				}
			})
			.catch(() => {
				if (active) {
					setError(true);
					setLoading(false);
				}
			});

		return () => {
			active = false;
		};
	}, [code]);

	const handleUseTemplate = () => {
		void TemplateCommands.openAcceptModal(code);
	};

	if (loading) {
		return (
			<EmbedCard
				icon={<EmbedSkeletonCircle data-flx="channel.template-embed.skeleton-icon" />}
				title={<EmbedSkeletonTitle data-flx="channel.template-embed.skeleton-title" />}
				subtitle={<EmbedSkeletonSubtitle data-flx="channel.template-embed.skeleton-subtitle" />}
				footer={<EmbedSkeletonButton data-flx="channel.template-embed.skeleton-button" />}
				data-flx="channel.template-embed.embed-card.loading"
			/>
		);
	}

	if (error || !template) {
		return (
			<EmbedCard
				icon={
					<div className={styles.iconCircle} data-flx="channel.template-embed.error-icon-circle">
						<WarningCircleIcon size={24} weight="bold" className={styles.iconOnBrand} data-flx="channel.template-embed.icon-on-brand" />
					</div>
				}
				title={
					<h3 className={`${cardStyles.title} ${cardStyles.titlePrimary}`} data-flx="channel.template-embed.error-title">
						{i18n._(TEMPLATE_UNAVAILABLE_TITLE_DESCRIPTOR)}
					</h3>
				}
				subtitle={
					<span className={cardStyles.helpText} data-flx="channel.template-embed.error-subtitle">
						{i18n._(TEMPLATE_UNAVAILABLE_SUBTITLE_DESCRIPTOR)}
					</span>
				}
				footer={
					<Button
						variant="secondary"
						fitContainer
						disabled
						data-flx="channel.template-embed.button.unavailable"
					>
						{i18n._(TEMPLATE_UNAVAILABLE_TITLE_DESCRIPTOR)}
					</Button>
				}
				data-flx="channel.template-embed.embed-card.error"
			/>
		);
	}

	const sourceGuild = template.serialized_source_guild;
	const channelCount = sourceGuild?.channels ? sourceGuild.channels.length : 0;
	const roleCount = sourceGuild?.roles ? sourceGuild.roles.length : 0;

	return (
		<EmbedCard
			icon={
				<div className={styles.iconCircle} data-flx="channel.template-embed.icon-circle">
					<CopyIcon size={24} weight="bold" className={styles.iconOnBrand} data-flx="channel.template-embed.icon-on-brand--2" />
				</div>
			}
			title={
				<h3 className={`${cardStyles.title} ${cardStyles.titlePrimary}`} data-flx="channel.template-embed.title">
					{template.name}
				</h3>
			}
			subtitle={
				<span className={cardStyles.helpText} data-flx="channel.template-embed.subtitle">
					{i18n._(SERVER_TEMPLATE_DESCRIPTOR)}
				</span>
			}
			body={
				<div data-flx="channel.template-embed.body">
					{template.description && (
						<div className={styles.description} data-flx="channel.template-embed.description">
							{template.description}
						</div>
					)}
					<div className={styles.stats} data-flx="channel.template-embed.stats">
						<span data-flx="channel.template-embed.span">
							<Plural value={channelCount} one="# channel" other="# channels" data-flx="channel.template-embed.plural" />
						</span>
						<span className={styles.statDot} data-flx="channel.template-embed.stat-dot" />
						<span data-flx="channel.template-embed.span--2">
							<Plural value={roleCount} one="# role" other="# roles" data-flx="channel.template-embed.plural--2" />
						</span>
					</div>
				</div>
			}
			footer={
				<Button
					variant="primary"
					fitContainer
					matchSkeletonHeight
					onClick={handleUseTemplate}
					data-flx="channel.template-embed.button.use-template"
				>
					{i18n._(USE_TEMPLATE_DESCRIPTOR)}
				</Button>
			}
			data-flx="channel.template-embed.embed-card"
		/>
	);
});

export default TemplateEmbed;
